import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { fetchContactStats } from '../../store/ContactSlice';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { 
  MdOutlineHealthAndSafety, 
  MdOutlineAssignment, 
  MdOutlinePeopleAlt, 
  MdOutlineCalendarMonth, 
  MdOutlineMarkEmailRead, 
  MdOutlineSupportAgent, 
  MdOutlineSettings,
  MdClose
} from "react-icons/md";

// Type-safe icon components for React 18 + TS 4.9 JSX compatibility
const CloseIcon = MdClose as React.ElementType;
const HealthIcon = MdOutlineHealthAndSafety as React.ElementType;
const AssignmentIcon = MdOutlineAssignment as React.ElementType;
const PeopleIcon = MdOutlinePeopleAlt as React.ElementType;
const CalendarIcon = MdOutlineCalendarMonth as React.ElementType;
const NewsletterIcon = MdOutlineMarkEmailRead as React.ElementType;
const ContactIcon = MdOutlineSupportAgent as React.ElementType;
const SettingsIcon = MdOutlineSettings as React.ElementType;

interface SidebarProps { 
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch<AppDispatch>();
  const pendingCount = useSelector((state: RootState) => state.contact.pendingCount);

  useEffect(() => {
    dispatch(fetchContactStats());
  }, [dispatch, pathname]);

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
  );

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector('body')?.classList.add('sidebar-expanded');
    } else {
      document.querySelector('body')?.classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);


  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-xs lg:hidden transition-opacity"
        />
      )}

      <aside
        ref={sidebar}
        className={`fixed lg:static left-0 top-0 z-50 flex h-screen w-72 flex-col overflow-y-hidden bg-[#041B44] duration-300 ease-linear shadow-xl lg:shadow-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* <!-- SIDEBAR HEADER --> */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-[#7586A6] shrink-0">  
          <NavLink
            to="/admin/dashboard"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center hover:opacity-90 transition-opacity"
            title="Centre SEVA Dashboard"
          >
            <img src="/images/Seva-logo.png" alt="Centre SEVA Logo" className="h-10 w-auto object-contain" /> 
          </NavLink>

          <button
            ref={trigger}
            onClick={() => setSidebarOpen(false)}
            aria-controls="sidebar"
            aria-label="Close sidebar"
            className="flex items-center justify-center lg:hidden text-[#B0BEC5] hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" 
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
          <nav className="py-2 px-4">  
            <div>
              <ul className="mb-6 flex flex-col items-start text-left justify-start w-full">
              
                <li className="w-full">
                  <NavLink
                    to="/admin/dashboard"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('dashboard') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <HealthIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.healthConditions")}</span>
                  </NavLink>
                </li>          
                <li className="w-full">
                  <NavLink
                    to="/admin/programs"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('program') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <AssignmentIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.programs")}</span>
                  </NavLink>
                </li>
                <li className="w-full">
                  <NavLink
                    to="/admin/patients"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('patients') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <PeopleIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.patients")}</span>
                  </NavLink>
                </li>
                <li className="w-full">  
                  <NavLink
                    to="/admin/bookings"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('bookings') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <CalendarIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.bookings")}</span>
                  </NavLink>
                </li>
                <li className="w-full">  
                  <NavLink
                    to="/admin/newsletter"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('newsletter') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <NewsletterIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.newsletter", "Newsletter")}</span>
                  </NavLink>
                </li>
                <li className="w-full">  
                  <NavLink
                    to="/admin/contacts"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('contact') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <ContactIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.contacts", "Contact Requests")}</span>
                    {pendingCount > 0 && (
                      <span className="ml-auto bg-[#E53E3E] text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-xs">
                        {pendingCount}
                      </span>
                    )}
                  </NavLink>
                </li>
                <li className="w-full">  
                  <NavLink
                    to="/admin/settings"
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center w-full px-4 py-3 rounded-md font-semibold whitespace-nowrap mb-4 hover:text-white hover:bg-[#B4804A] ${
                      pathname.includes('settings') ? 'text-white bg-[#B4804A]' : 'text-[#B0BEC5]'
                    }`}
                  >
                    <SettingsIcon className="text-2xl mr-3 shrink-0" />
                    <span className="whitespace-nowrap">{t("adminNav.settings", "Settings")}</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        {/* SIDEBAR FOOTER: Language Switcher (accessible on mobile/tablet) */}
        <div className="p-4 border-t border-[#7586A6]/30 flex items-center justify-between bg-[#021330]/70 mt-auto shrink-0">
          <span className="text-xs font-semibold text-[#B0BEC5] uppercase tracking-wider">
            {t("common.language", "Language")}
          </span>
          <LanguageSwitcher theme="dark" />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;